/**
 * guidePageSchema.js — JSON-LD for the permanent /guides/* pages.
 *
 * SEOHead already emits Organization, LocalBusiness and the path-derived
 * BreadcrumbList. This adds WebPage + Article. Guides are maintained
 * resource pages, so deliberately:
 *   - Article, never BlogPosting (they are not blog posts);
 *   - no Product / Review / AggregateRating, even though providers are
 *     compared — the page reviews nothing and rates no one;
 *   - no FAQPage: the FAQs stay visible content only (FAQ rich results
 *     were withdrawn in May 2026, and the handover asks us not to rely on
 *     them);
 *   - datePublished only once the guide has a real launch date — never
 *     invented or back-dated. dateModified tracks the last content review.
 */

const PROD_URL = process.env.REACT_APP_PRODUCTION_URL || "https://candopayments.com";

export function buildGuideListSchema(hub, guides) {
  const url = `${PROD_URL}${hub.path}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${url}#webpage`,
      url,
      name: hub.h1,
      description: hub.metaDescription,
      inLanguage: "en-GB",
      isPartOf: { "@id": `${PROD_URL}/#website` },
      publisher: { "@id": `${PROD_URL}/#organization` },
      hasPart: guides.map((g) => ({ "@id": `${PROD_URL}${g.path}#article` })),
    },
  ];
}

export function buildGuidePageSchema(guide, description) {
  const url = `${PROD_URL}${guide.path}`;
  // ISO dates compare as strings; modified is never earlier than published.
  const dateModified = [guide.reviewed, guide.published].filter(Boolean).sort().pop();

  const webPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: guide.h1,
    description,
    inLanguage: "en-GB",
    isPartOf: { "@id": `${PROD_URL}/#website` },
    mainEntity: { "@id": `${url}#article` },
    ...(dateModified && { dateModified }),
  };

  const article = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    headline: guide.h1,
    description,
    url,
    mainEntityOfPage: { "@id": `${url}#webpage` },
    inLanguage: "en-GB",
    // The guide is published by the company, not a named individual — the
    // handover gives no author and we don't invent one.
    author: { "@id": `${PROD_URL}/#organization` },
    publisher: { "@id": `${PROD_URL}/#organization` },
    ...(guide.published && { datePublished: guide.published }),
    ...(dateModified && { dateModified }),
  };

  return [webPage, article];
}
